import { t } from "../i18n";

/**
 * A condition block's rules ask about the contact and the conversation (a tag, a field, the
 * message just received, the person attending, WhatsApp's 24-hour window) or about a variable the
 * flow gathered. Each subject is a path the engine reads; the builder shows it by name.
 */
export type ConditionSubject =
  "tag" | "field" | "message" | "assignee" | "window" | "variable";

export const CONDITION_SUBJECTS: ConditionSubject[] = [
  "tag",
  "field",
  "message",
  "assignee",
  "window",
  "variable",
];

export interface ConditionRule {
  variable: string;
  op: string;
  value?: unknown;
  next?: string;
}

export function subjectOf(
  r: Pick<ConditionRule, "variable">,
): ConditionSubject {
  if (r.variable === "contact.tagIds") return "tag";
  if (r.variable.startsWith("contact.fields.")) return "field";
  if (r.variable === "message.text") return "message";
  if (r.variable === "conversation.assigneeId") return "assignee";
  if (r.variable === "conversation.windowOpen") return "window";
  return "variable";
}

/** "Etiqueta: tem VIP", "Atendente é Bia", "Janela de 24 h aberta": the rule as its exit reads. */
export function ruleLabel(
  r: ConditionRule,
  names: {
    tags: Record<string, string>;
    members: Record<string, string>;
    fields?: Record<string, string>;
  },
): string {
  const value = String(r.value ?? "");
  const op = t(`op.${r.op}` as never);
  switch (subjectOf(r)) {
    case "tag":
      return t(
        r.op === "not_contains"
          ? "node.condition.label.tag_without"
          : "node.condition.label.tag_with",
        { tag: names.tags[value] ?? "?" },
      );
    case "assignee":
      return r.op === "exists"
        ? t("node.condition.label.assignee_any")
        : t(
            r.op === "neq"
              ? "node.condition.label.assignee_not"
              : "node.condition.label.assignee_is",
            { name: names.members[value] ?? "?" },
          );
    case "window":
      return value === "false"
        ? t("node.condition.label.window_closed")
        : t("node.condition.label.window_open");
    case "message":
      return `${t("node.condition.subject.message")} ${op} ${value}`.trim();
    case "field": {
      const key = r.variable.replace(/^contact\.fields\./, "");
      return `${names.fields?.[key] ?? key} ${op} ${value}`.trim();
    }
    default:
      return `${r.variable || "?"} ${op} ${value}`.trim();
  }
}
